import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import {
  Settings,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  DollarSign,
  Clock,
  Sliders,
  Users,
  Trash2,
  Plus,
  Save,
  BarChart3,
  AlertCircle,
} from "lucide-react";

const JOB_LABELS: Record<string, string> = {
  ingestTenders_BOAMP: "Ingestion BOAMP",
  processScheduledReminders: "Relances automatiques",
  processWebhookRetries: "Retry Webhooks",
  processRecurringInvoices: "Factures récurrentes",
  processUnreadMessages: "Notifications messages",
};

const JOBS = Object.keys(JOB_LABELS) as Array<
  "ingestTenders_BOAMP" | "processScheduledReminders" | "processWebhookRetries" | "processRecurringInvoices" | "processUnreadMessages"
>;

function StatusBadge({ status }: { status: string }) {
  if (status === "SUCCESS")
    return <Badge variant="success">SUCCESS</Badge>;
  if (status === "FAILED")
    return <Badge variant="destructive">FAILED</Badge>;
  if (status === "RUNNING")
    return <Badge variant="secondary">RUNNING</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function HealthIcon({ status }: { status: "ok" | "warn" | "error" }) {
  if (status === "ok") return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  if (status === "error") return <XCircle className="h-5 w-5 text-destructive" />;
  return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
}

function MaxProsConfigSection() {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newOverride, setNewOverride] = useState({ category: "", maxPros: "", rotation: "" });
  const [editingOverride, setEditingOverride] = useState<string | null>(null);
  const [overrideEdit, setOverrideEdit] = useState({ maxPros: "", rotation: "" });

  const { data: config, isLoading: configLoading, refetch: refetchConfig } = trpc.admin.systemConfig.get.useQuery();
  const { data: overridesData, isLoading: overridesLoading, refetch: refetchOverrides } = trpc.admin.systemConfig.getCategoryOverrides.useQuery();
  const { data: rotation, isLoading: rotationLoading } = trpc.admin.travauxbtp.getRotationMonitoring.useQuery();

  const setConfig = trpc.admin.systemConfig.set.useMutation({
    onSuccess: () => { setEditingKey(null); refetchConfig(); },
  });
  const setCategoryOverride = trpc.admin.systemConfig.setCategoryOverride.useMutation({
    onSuccess: () => { setNewOverride({ category: "", maxPros: "", rotation: "" }); setEditingOverride(null); refetchOverrides(); },
  });
  const deleteCategoryOverride = trpc.admin.systemConfig.deleteCategoryOverride.useMutation({
    onSuccess: () => refetchOverrides(),
  });

  const SETTING_LABELS: Record<string, { label: string; unit: string }> = {
    maxProsPerProject: { label: "Max artisans par projet", unit: "artisans" },
    rotationThresholdPerWeek: { label: "Seuil rotation / semaine", unit: "leads" },
    staleProjectDelayHours: { label: "Délai projet stale", unit: "heures" },
  };

  return (
    <>
      {/* Global Config */}
      <div>
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Sliders className="h-4 w-4" />
          Configuration Max Pros / Rotation
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          {configLoading
            ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
            : config?.settings && Object.entries(config.settings).map(([key, setting]) => {
                const meta = SETTING_LABELS[key] || { label: key, unit: "" };
                const isEditing = editingKey === key;
                return (
                  <Card key={key}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{meta.label}</div>
                        <Badge variant="outline" className="text-xs">défaut: {setting.default}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{setting.description}</div>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            className="w-20 border rounded px-2 py-1 text-sm bg-background"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            min={1}
                            max={1000}
                          />
                          <span className="text-xs text-muted-foreground">{meta.unit}</span>
                          <Button
                            size="sm"
                            onClick={() => {
                              const val = parseInt(editValue);
                              if (!isNaN(val) && val >= 1 && val <= 1000) {
                                setConfig.mutate({ key, value: val });
                              }
                            }}
                            disabled={setConfig.isPending}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            OK
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingKey(null)}>
                            Annuler
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold">{setting.value} <span className="text-sm font-normal text-muted-foreground">{meta.unit}</span></span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingKey(key); setEditValue(String(setting.value)); }}
                          >
                            Modifier
                          </Button>
                        </div>
                      )}
                      {setting.updatedAt && (
                        <div className="text-xs text-muted-foreground">Modifié le {formatDate(setting.updatedAt)}</div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
        </div>
      </div>

      {/* Category Overrides */}
      <div>
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Overrides par catégorie
        </h2>
        <Card>
          <CardContent className="p-0">
            {overridesLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : (
              <>
                {(!overridesData?.overrides || overridesData.overrides.length === 0) ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    Aucun override — les paramètres globaux s'appliquent à toutes les catégories
                  </div>
                ) : (
                  <div className="divide-y">
                    <div className="grid grid-cols-4 gap-4 px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50">
                      <div>Catégorie</div>
                      <div>Max artisans</div>
                      <div>Seuil rotation</div>
                      <div className="text-right">Actions</div>
                    </div>
                    {overridesData.overrides.map((ov) => {
                      const isEditing = editingOverride === ov.category;
                      return (
                        <div key={ov.category} className="grid grid-cols-4 gap-4 items-center px-4 py-3 text-sm">
                          <div className="font-medium capitalize">{ov.category}</div>
                          {isEditing ? (
                            <>
                              <input
                                type="number"
                                className="w-20 border rounded px-2 py-1 text-sm bg-background"
                                value={overrideEdit.maxPros}
                                onChange={(e) => setOverrideEdit({ ...overrideEdit, maxPros: e.target.value })}
                                placeholder="—"
                                min={1}
                                max={50}
                              />
                              <input
                                type="number"
                                className="w-20 border rounded px-2 py-1 text-sm bg-background"
                                value={overrideEdit.rotation}
                                onChange={(e) => setOverrideEdit({ ...overrideEdit, rotation: e.target.value })}
                                placeholder="—"
                                min={1}
                                max={200}
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const maxPros = overrideEdit.maxPros ? parseInt(overrideEdit.maxPros) : null;
                                    const rot = overrideEdit.rotation ? parseInt(overrideEdit.rotation) : null;
                                    setCategoryOverride.mutate({
                                      category: ov.category,
                                      maxProsPerProject: maxPros,
                                      rotationThresholdPerWeek: rot,
                                    });
                                  }}
                                  disabled={setCategoryOverride.isPending}
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingOverride(null)}>
                                  Annuler
                                </Button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>{ov.maxProsPerProject ?? <span className="text-muted-foreground">—</span>}</div>
                              <div>{ov.rotationThresholdPerWeek ?? <span className="text-muted-foreground">—</span>}</div>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingOverride(ov.category);
                                    setOverrideEdit({
                                      maxPros: ov.maxProsPerProject != null ? String(ov.maxProsPerProject) : "",
                                      rotation: ov.rotationThresholdPerWeek != null ? String(ov.rotationThresholdPerWeek) : "",
                                    });
                                  }}
                                >
                                  Modifier
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive"
                                  onClick={() => {
                                    if (confirm(`Supprimer l'override pour "${ov.category}" ?`)) {
                                      deleteCategoryOverride.mutate({ category: ov.category });
                                    }
                                  }}
                                  disabled={deleteCategoryOverride.isPending}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Add override form */}
                <div className="border-t p-4 flex items-end gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Catégorie</label>
                    <input
                      className="w-40 border rounded px-3 py-1.5 text-sm bg-background"
                      value={newOverride.category}
                      onChange={(e) => setNewOverride({ ...newOverride, category: e.target.value })}
                      placeholder="ex: plomberie"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Max artisans</label>
                    <input
                      type="number"
                      className="w-24 border rounded px-3 py-1.5 text-sm bg-background"
                      value={newOverride.maxPros}
                      onChange={(e) => setNewOverride({ ...newOverride, maxPros: e.target.value })}
                      placeholder="—"
                      min={1}
                      max={50}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Seuil rotation</label>
                    <input
                      type="number"
                      className="w-24 border rounded px-3 py-1.5 text-sm bg-background"
                      value={newOverride.rotation}
                      onChange={(e) => setNewOverride({ ...newOverride, rotation: e.target.value })}
                      placeholder="—"
                      min={1}
                      max={200}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!newOverride.category.trim()) return;
                      const maxPros = newOverride.maxPros ? parseInt(newOverride.maxPros) : null;
                      const rot = newOverride.rotation ? parseInt(newOverride.rotation) : null;
                      if (maxPros === null && rot === null) return;
                      setCategoryOverride.mutate({
                        category: newOverride.category.trim(),
                        maxProsPerProject: maxPros,
                        rotationThresholdPerWeek: rot,
                      });
                    }}
                    disabled={setCategoryOverride.isPending || !newOverride.category.trim()}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rotation Analytics */}
      <div>
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Analytics rotation
        </h2>
        {rotationLoading ? (
          <div className="grid gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : rotation ? (
          <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid gap-3 md:grid-cols-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Taux de remplissage moyen</div>
                  <div className="text-2xl font-bold">{Math.round(rotation.avgFillRate * 100)}%</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Temps moyen remplissage</div>
                  <div className="text-2xl font-bold">{rotation.avgTimeToFillHours != null ? `${rotation.avgTimeToFillHours}h` : "—"}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Projets complets</div>
                  <div className="text-2xl font-bold text-green-600">{rotation.projectsComplete}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">Projets en attente</div>
                  <div className="text-2xl font-bold text-yellow-600">{rotation.projectsWaiting}</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Top 10 Pros */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Top 10 pros — leads cette semaine
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {rotation.leadDistribution.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">Aucune donnée</div>
                  ) : (
                    <div className="divide-y">
                      {rotation.leadDistribution.slice(0, 10).map((pro, i) => (
                        <div key={pro.companyId} className="flex items-center justify-between px-4 py-2 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                            <span className="truncate max-w-[200px]">{pro.companyName}</span>
                          </div>
                          <Badge variant="secondary">{pro.leadsThisWeek} leads</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stale Projects */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                    Projets sous-pourvus (stale)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {rotation.staleProjects.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">Aucun projet stale</div>
                  ) : (
                    <div className="divide-y">
                      {rotation.staleProjects.slice(0, 10).map((proj) => (
                        <div key={proj.id} className="px-4 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="truncate max-w-[200px] font-medium">{proj.title}</span>
                            <Badge variant="destructive">{proj.hoursStale}h</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {proj.category} · {proj.department} · {proj.unlocksCount}/{proj.maxUnlocks} artisans
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Rotation Alerts */}
            {rotation.rotationAlerts.length > 0 && (
              <Card className="border-yellow-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    Alertes rotation — pros surreprésentés (&gt;2x la moyenne)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {rotation.rotationAlerts.map((alert) => (
                      <div key={alert.companyId} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="truncate max-w-[200px]">{alert.companyName}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">moy: {alert.avgLeadsPerPro}</span>
                          <Badge variant="outline" className="text-yellow-700">{alert.leadsThisWeek} leads ({alert.ratio}x)</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

export default function SystemSettings() {
  const [triggering, setTriggering] = useState<string | null>(null);
  const [editPricing, setEditPricing] = useState<{ key: string; value: string; description: string } | null>(null);
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");

  const { data: jobs, isLoading: jobsLoading, refetch: refetchJobs } = trpc.admin.systemSettings.jobRuns.useQuery({ limit: 50 });
  const { data: health, isLoading: healthLoading, refetch: refetchHealth } = trpc.admin.systemSettings.health.useQuery();
  const { data: pricing, isLoading: pricingLoading, refetch: refetchPricing } = trpc.admin.systemSettings.pricingList.useQuery();

  const triggerJob = trpc.admin.systemSettings.triggerJob.useMutation({
    onSuccess: () => {
      setTriggering(null);
      setTimeout(() => refetchJobs(), 2000);
    },
    onError: () => setTriggering(null),
  });

  const pricingUpsert = trpc.admin.systemSettings.pricingUpsert.useMutation({
    onSuccess: () => {
      setEditPricing(null);
      setNewKey("");
      setNewVal("");
      refetchPricing();
    },
  });

  const pricingDelete = trpc.admin.systemSettings.pricingDelete.useMutation({
    onSuccess: () => refetchPricing(),
  });

  const handleTrigger = (jobName: typeof JOBS[number]) => {
    setTriggering(jobName);
    triggerJob.mutate({ jobName });
  };

  // Latest run per job name
  const latestByJob: Record<string, any> = {};
  for (const job of jobs ?? []) {
    const base = job.name.replace(/^manual_/, "");
    if (!latestByJob[base]) latestByJob[base] = job;
  }

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Paramètres système</h1>
            <p className="text-muted-foreground">Supervision des jobs planifiés, santé du système et configuration</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { refetchJobs(); refetchHealth(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Health Checks */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Santé du système
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {healthLoading
              ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
              : health?.map((check) => (
                  <Card key={check.service}>
                    <CardContent className="flex items-center gap-3 p-4">
                      <HealthIcon status={check.status} />
                      <div>
                        <div className="font-medium text-sm capitalize">{check.service.replace(/_/g, " ")}</div>
                        {check.detail && (
                          <div className="text-xs text-muted-foreground">{check.detail}</div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
          </div>
        </div>

        {/* Jobs */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Jobs planifiés
          </h2>
          <div className="space-y-3">
            {JOBS.map((jobName) => {
              const latest = latestByJob[jobName];
              const isTrig = triggering === jobName;
              return (
                <Card key={jobName}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{JOB_LABELS[jobName]}</div>
                        <div className="text-sm text-muted-foreground">
                          {latest
                            ? `Dernier run : ${formatDate(latest.startedAt)} — ${latest.itemsProcessed ?? 0} éléments`
                            : "Jamais exécuté"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {latest && <StatusBadge status={latest.status} />}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isTrig || triggerJob.isPending}
                        onClick={() => handleTrigger(jobName as typeof JOBS[number])}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        {isTrig ? "En cours…" : "Déclencher"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Recent Job Runs Log */}
        <div>
          <h2 className="text-base font-semibold mb-3">Historique des jobs (50 derniers)</h2>
          <Card>
            <CardContent className="p-0">
              {jobsLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : !jobs || jobs.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">Aucun job enregistré</div>
              ) : (
                <div className="divide-y">
                  {jobs.slice(0, 20).map((job: any) => (
                    <div key={job.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <div className="flex items-center gap-3">
                        <StatusBadge status={job.status} />
                        <span className="font-medium">{job.name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-muted-foreground text-xs">
                        {job.itemsProcessed != null && <span>{job.itemsProcessed} éléments</span>}
                        {job.errorCount != null && job.errorCount > 0 && (
                          <span className="text-destructive">{job.errorCount} erreur(s)</span>
                        )}
                        <span>{formatDate(job.startedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pricing Config */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Configuration tarifaire
          </h2>
          <Card>
            <CardContent className="p-0">
              {pricingLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : (
                <>
                  <div className="divide-y">
                    {(pricing ?? []).map((p: any) => (
                      <div key={p.key} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div>
                          <div className="font-medium">{p.key}</div>
                          {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{p.value}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditPricing({ key: p.key, value: p.value, description: p.description ?? "" })}
                          >
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => {
                              if (confirm(`Supprimer la clé "${p.key}" ?`)) {
                                pricingDelete.mutate({ key: p.key });
                              }
                            }}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Add / Edit form */}
                  <div className="border-t p-4 flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-muted-foreground mb-1">Clé</label>
                      <input
                        className="w-full border rounded px-3 py-1.5 text-sm bg-background"
                        value={editPricing ? editPricing.key : newKey}
                        readOnly={!!editPricing}
                        onChange={(e) => editPricing ? setEditPricing({ ...editPricing, key: e.target.value }) : setNewKey(e.target.value)}
                        placeholder="ex: plan_pro_monthly"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-muted-foreground mb-1">Valeur</label>
                      <input
                        className="w-full border rounded px-3 py-1.5 text-sm bg-background"
                        value={editPricing ? editPricing.value : newVal}
                        onChange={(e) => editPricing ? setEditPricing({ ...editPricing, value: e.target.value }) : setNewVal(e.target.value)}
                        placeholder="ex: 49"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        const key = editPricing ? editPricing.key : newKey.trim();
                        const value = editPricing ? editPricing.value : newVal.trim();
                        if (!key || !value) return;
                        pricingUpsert.mutate({ key, value, description: editPricing?.description });
                      }}
                      disabled={pricingUpsert.isPending}
                    >
                      {editPricing ? "Enregistrer" : "Ajouter"}
                    </Button>
                    {editPricing && (
                      <Button size="sm" variant="outline" onClick={() => setEditPricing(null)}>
                        Annuler
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Max Pros / Rotation Config */}
        <MaxProsConfigSection />
      </div>
    </AdminLayout>
  );
}
